<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import { projects, getAdjacentProjects } from '$lib/projects';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let studentsCount = 0;
  let partnersCount = 0;
  let semestersCount = 0;

  // Get the current project slug from the URL
  const currentSlug = 'framing-art-science-technology';
  const navigation = getAdjacentProjects(currentSlug);

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 24, 2000, (val) => studentsCount = val);
          animateValue(0, 12, 1000, (val) => partnersCount = val);
          animateValue(0, 4, 1500, (val) => semestersCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    // Cleanup function
    return () => {
      metricsObserver.disconnect();
    };
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Framing Art Science Technology"
    client="University of Applied Sciences Berlin"
    industry="Education"
    product="MA Program Development"
    heroMedia={{
      type: "image",
      src: "/projects/fast/fast-key.png"
    }}
    metrics={[
      { value: 24, label: "Students per Year" },
      { value: 12, label: "Partner Institutions" },
      { value: 4, label: "Semesters" }
    ]}
    roles={[
      "Program Development",
      "Curriculum Design",
      "Educational Innovation"
    ]}
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6">
      <p class="large-text text-black/90 animate-on-view">
        A pioneering postgraduate program that bridges the gap between art, science, and emerging technologies.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="text-gray-700">
            How can we create an educational program that meaningfully integrates art, science, and technology while preparing students for future challenges?
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">Delivered Value</h2>
          <p class="mb-8 text-gray-700">
            A comprehensive curriculum that bridges different disciplines, providing students with practical skills and theoretical knowledge needed to work at the intersection of art, science, and technology.
          </p>
        </div>
      </div>
    </div>

    <!-- Key Features Section -->
    <div class="w-full mb-16 mt-16">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-8">
        <FeatureCell
          icon="book"
          title="Interdisciplinary Curriculum"
          description="Integration of art, science, and technology in a unified program"
        />
        
        <FeatureCell
          icon="users"
          title="Partner Network"
          description="Collaboration with 12+ leading institutions and industry partners"
        />

        <FeatureCell
          icon="lab"
          title="Research Focus"
          description="Emphasis on practical research and experimental approaches"
        />

        <FeatureCell
          icon="graduation-cap"
          title="Career Development"
          description="Preparation for emerging roles in art-science-technology"
        />
      </div>
    </div>

    <!-- Quote Section -->
    <section class="py-8 animate-on-view">
      <div class="container px-4 md:px-8 py-16 max-w-4xl">
        <div class="grid grid-cols-12">
          <div class="col-span-12">
            <div class="relative">
              <span 
                class="absolute -top-8 text-[120px] leading-none text-[#F96604] font-serif animate-on-view"
                aria-hidden="true"
                style="opacity: 0.1"
              >
                ”
              </span>
              <blockquote class="py-12 text-xl md:text-4xl font-light animate-on-view" style="color: hsla(226, 65%, 35%, 1); line-height: 1.4;">
                The program creates a unique space where art meets science, fostering innovation and preparing students for the challenges of tomorrow.
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</div>

<!-- Program Diagram -->
<section class="project-section bg-gray-50">
  <div class="container mx-auto px-8 md:px-16">
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <h2 class="large-text text-black/90">Program Structure</h2>
    </div>
    <!-- Main Diagram -->
    <div class="w-full overflow-hidden rounded-lg shadow-lg animate-on-view mb-12">
      <img 
        src="/projects/framing-art-science-technology/itto–ablauf-diagram_A3.png" 
        alt="MA Program Structure Diagram" 
        class="w-full h-auto"
      />
    </div>
    <!-- Arduino Image -->
    <div class="w-full max-w-md mx-auto overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.2">
      <img 
        src="/projects/framing-art-science-technology/arduino.png" 
        alt="Arduino Project Example" 
        class="w-full h-auto"
      />
    </div>
  </div>
</section>

<!-- Event Content -->
<div class="min-h-screen bg-white">
  <!-- Event Hero -->
  <section class="project-section">
    <div class="container mx-auto px-8 md:px-16">
      <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
        <h2 class="large-text text-black/90">Revolving Stars & Shaky Grounds</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <p class="text-lg text-black/90 font-light mb-8">
            Organization, curation, and art direction of an interdisciplinary forum at the intersection of technology and art.
          </p>
          <p class="text-lg text-black/90 font-light mb-8">
            How does art deal with the highly technical conditions of thinking and knowledge in the 21st century? What does it mean to speculate with reality? Does data mining hold utopian potential? Will the apocalypse be televised? And is the future really not set?
          </p>
          <p class="text-lg text-black/90 font-light">
            42 artists, scientists, technicians, theorists, and other visionaries discuss and present constellations of speculative thinking and knowledge in uncertain times.
          </p>
        </div>
        
        <div>
          <div class="space-y-2 mb-8">
            <p class="section-label">Event Details</p>
            <p class="text-lg text-black/90 font-light">
              The event takes place from July 3rd to July 6th at Festspielhaus Hellerau (Dresden).
            </p>
          </div>
          <div class="space-y-2">
            <p class="section-label">Host</p>
            <p class="text-lg text-black/90 font-light">
              F-A-S-T Framing Art, Science & Technology, an artistic-technical-scientific pilot project of HfBK, TU, and HTW Dresden, inviting participants to an interdisciplinary forum.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Program Details -->
  <section class="project-section bg-gray-50">
    <div class="container mx-auto px-8 md:px-16">
      <div class="w-full overflow-hidden rounded-lg shadow-lg animate-on-view">
        <img 
          src="/projects/framing-art-science-technology/programm.png" 
          alt="Event Program Details" 
          class="w-full h-auto"
        />
      </div>
    </div>
  </section>

  <!-- Event Gallery -->
  <section class="project-section">
    <div class="container mx-auto px-8 md:px-16">
      <h2 class="text-2xl font-light mb-8">Event Documentation</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Column 1 -->
        <div class="space-y-4">
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0">
            <img 
              src="/projects/framing-art-science-technology/130503_Wopperer.jpg" 
              alt="Event Overview" 
              class="w-full h-auto"
            />
          </div>
        </div>
        
        <!-- Column 2 -->
        <div class="space-y-4">
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.1">
            <img 
              src="/projects/framing-art-science-technology/IMG_7433.jpeg" 
              alt="Event Space" 
              class="w-full h-auto"
            />
          </div>
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.2">
            <img 
              src="/projects/framing-art-science-technology/IMG_7435.jpeg" 
              alt="Event Detail" 
              class="w-full h-auto"
            />
          </div>
        </div>
        
        <!-- Column 3 -->
        <div class="space-y-4">
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.3">
            <img 
              src="/projects/framing-art-science-technology/IMG_7436.jpeg" 
              alt="Event Interaction" 
              class="w-full h-auto"
            />
          </div>
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.4">
            <img 
              src="/projects/framing-art-science-technology/IMG_7437.jpeg" 
              alt="Event Setup" 
              class="w-full h-auto"
            />
          </div>
        </div>
        
        <!-- Column 4 -->
        <div class="space-y-4">
          <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0.5">
            <img 
              src="/projects/framing-art-science-technology/plakat.png" 
              alt="Event Poster" 
              class="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</div>

<!-- Navigation & Footer -->
{#if navigation}
  <ProjectNav 
    currentProject={{
      title: navigation.currentProject.title,
      category: navigation.currentProject.category,
      link: navigation.currentProject.link
    }}
    allProjects={projects}
  />
{/if}
<Footer />

<style>
  /* Add any page-specific styles here */
</style> 